import React, { useState, useRef, useCallback, useEffect } from "react";
import "./index.css";
import "./App.css";

import Game from "./components/game/Game";

// --- Ripple Effect (เหมือนเดิม ไม่ต้องแก้) ---
function RippleEffect({ id, x, y, size, onRemove }) {
  useEffect(() => {
    const timer = setTimeout(() => onRemove(id), 600);
    return () => clearTimeout(timer);
  }, [id, onRemove]);

  return (
    <span
      className="absolute rounded-full bg-white/60 animate-ripple pointer-events-none"
      style={{ top: y, left: x, width: size, height: size }}
    />
  );
}

// --- Ripple Provider (เหมือนเดิม ไม่ต้องแก้) ---
function RippleProvider({ children, isUiMuted }) {
  const [ripples, setRipples] = useState([]);
  const rippleCleanupTimer = useRef(null);
  const clickAudioRef = useRef(new Audio("/sounds/button_click.mp3"));

  const addRipple = useCallback(
    (event) => {
      const target = event.target.closest('[data-sound-ignore="true"]');
      if (target) {
        return;
      }
      const clickedOnInteractable = event.target.closest(
        'button, a, input, [role="button"], [role="dialog"]'
      );
      if (clickedOnInteractable && !isUiMuted) {
        try {
          if (clickAudioRef.current) {
            clickAudioRef.current.currentTime = 0;
            clickAudioRef.current.volume = 0.4;
            clickAudioRef.current.play().catch(() => { });
          }
        } catch (e) { }
      }
      const size = 30;
      const x = event.clientX - size / 2;
      const y = event.clientY - size / 2;
      const newRipple = { id: Date.now(), x, y, size };
      setRipples((prev) => [...prev, newRipple]);
    },
    [isUiMuted]
  );
  const handleRemoveRipple = useCallback((id) => {
    setRipples((prev) => prev.filter((r) => r.id !== id));
  }, []);
  useEffect(() => {
    window.addEventListener("mousedown", addRipple);
    return () => window.removeEventListener("mousedown", addRipple);
  }, [addRipple]);
  useEffect(() => {
    if (ripples.length > 0) {
      clearTimeout(rippleCleanupTimer.current);
      rippleCleanupTimer.current = setTimeout(() => {
        setRipples([]);
      }, 1000);
    }
    return () => clearTimeout(rippleCleanupTimer.current);
  }, [ripples]);

  return (
    <div className="relative w-full min-h-screen">
      {children}
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-[9999]">
        {ripples.map((ripple) => (
          <RippleEffect
            key={ripple.id}
            {...ripple}
            onRemove={handleRemoveRipple}
          />
        ))}
      </div>
    </div>
  );
}

// ==========================================
// ส่วนหลัก App Component (Simple Splash Screen Version)
// ==========================================
export default function App() {
  // ✅ 1. แก้ไขจุดนี้: เช็ค sessionStorage ก่อนกำหนดค่าเริ่มต้น
  const [hasInteracted, setHasInteracted] = useState(() => {
    return sessionStorage.getItem("hasVisitedGame") === "true";
  });

  const [soundSettings, setSoundSettings] = useState({
    isUiMuted: false,
    isBgmMuted: false,
    isAmbienceMuted: false,
    isVoiceMuted: false,
    isVibrationMuted: false,
  });

  useEffect(() => {
    const savedSettings = localStorage.getItem("soundSettings");
    if (savedSettings) {
      setSoundSettings((prev) => ({ ...prev, ...JSON.parse(savedSettings) }));
    }
  }, []);

  const handleUpdateSoundSettings = (key, value) => {
    setSoundSettings((prev) => {
      const newSettings = { ...prev, [key]: value };
      localStorage.setItem("soundSettings", JSON.stringify(newSettings));
      return newSettings;
    });
  };

  const handleStartInteraction = () => {
    // ✅ 2. บันทึกว่าเคยกดแล้วลงใน sessionStorage
    sessionStorage.setItem("hasVisitedGame", "true");
    setHasInteracted(true);
    // ปลดล็อค Audio Context ด้วยเสียงเงียบๆ
    const silentAudio = new Audio("/sounds/unlock_audio.mp3");
    silentAudio.volume = 0;
    silentAudio.play().catch(() => { });
  };

  // ------------------------------------------------------------------
  // 🎨 SPLASH SCREEN แบบเรียบง่าย (โชว์ถ้ายังไม่กด hasInteracted)
  // ------------------------------------------------------------------
  if (!hasInteracted) {
    return (
      <div
        onClick={handleStartInteraction}
        className="fixed inset-0 z-[99999] flex flex-col items-center justify-center cursor-pointer text-[#2f2218]"
        style={{
          backgroundColor: "#f3e9d2",
          backgroundImage: 'url("/images/desk_background.jpg")', // ✅ พื้นหลังเดียวกับเกม
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        {/* 1. โลโก้ (ใหญ่สะใจ) */}
        <div className="mb-10 animate-pulse">
          <img
            src="/logo.png"
            alt="Game Logo"
            className="w-80 md:w-[32rem] h-auto drop-shadow-2xl" // ปรับขนาด w-80 (มือถือ) / w-[32rem] (จอใหญ่)
            onError={(e) => {
              e.target.style.display = "none";
            }}
          />
        </div>

        {/* 2. ข้อความแตะเพื่อเริ่ม */}
        <div className="flex flex-col items-center gap-2 drop-shadow-md text-center">
          <h1 className="text-xl font-bold font-serif tracking-widest uppercase border-b-4 border-[#2f2218] pb-2">
            แตะเพื่อเริ่มเกม
          </h1>
          <p className="text-l font-bold font-sans tracking-wide opacity-80">
            TAP ANYWHERE TO START
          </p>
        </div>

        {/* 3. เครดิต (อยู่ล่างสุด) */}
        <div className="absolute bottom-6 w-full text-center opacity-60">
          <p
            className="mt-4 text-xs text-center text-gray-300 font-mono tracking-wide opacity-80"
            style={{ textShadow: "1px 1px 2px black" }}
          >
            สร้างโดย Google Gemini
            <svg
              viewBox="0 0 24 24"
              className="inline-block w-4 h-4 mx-1"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <defs>
                <linearGradient
                  id="geminiBluePurple"
                  x1="0%"
                  y1="50%"
                  x2="100%"
                  y2="50%"
                >
                  <stop offset="0%" stopColor="#7C3AED" />
                  <stop offset="100%" stopColor="#2563EB" />
                </linearGradient>
              </defs>
              <path
                d="M12 2L14.2 9.6C14.5 10.7 15.3 11.5 16.4 11.8L24 14L16.4 16.2C15.3 16.5 14.5 17.3 14.2 18.4L12 26L9.8 18.4C9.5 17.3 8.7 16.5 7.6 16.2L0 14L7.6 11.8C8.7 11.5 9.5 10.7 9.8 9.6L12 2Z"
                fill="url(#geminiBluePurple)"
                transform="scale(0.8) translate(6, 0)"
              />
              <path
                d="M18 4L18.9 7.1C19 7.5 19.3 7.8 19.7 7.9L22.8 8.8L19.7 9.7C19.3 9.8 19 10.1 18.9 10.5L18 13.6L17.1 10.5C17 10.1 16.7 9.8 16.3 9.7L13.2 8.8L16.3 7.9C16.7 7.8 17 7.5 17.1 7.1L18 4Z"
                fill="url(#geminiBluePurple)"
              />
            </svg>
            <br />@ Lonely Dog Studio (2025)
          </p>
          <p
            className="mt-2 text-[10px] text-center text-gray-400 font-sans tracking-wide opacity-75 max-w-[280px] sm:max-w-xs mx-auto leading-relaxed"
            style={{ textShadow: "1px 1px 2px black" }}
          >
            แอปพลิเคชันนี้จัดทำขึ้นเพื่อวัตถุประสงค์ทางการศึกษา Vibe Coding เท่านั้น โดยไม่มีวัตถุประสงค์ทางการค้าใด ๆ ทั้งสิ้น
          </p>
        </div>
      </div>
    );
  }

  // ------------------------------------------------------------------
  // 🎮 GAME SCREEN (โชว์เมื่อกดแล้ว)
  // ------------------------------------------------------------------
  return (
    <RippleProvider isUiMuted={soundSettings.isUiMuted}>
      <Game
        soundSettings={soundSettings}
        onUpdateSoundSettings={handleUpdateSoundSettings}
      />
    </RippleProvider>
  );
}
